import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import { Copy, Share2, Users2, Trophy, Check, Gem, UserRound } from 'lucide-react';
import { Capacitor, registerPlugin } from '@capacitor/core';

import BackArrow from '@/components/ui/BackArrow';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import ErrorDisplay from '@/components/ui/ErrorDisplay';
import { cn } from '@/utils/cn';
import { getMyReferrals, type ReferralsMeResponse, type ReferralStage } from '@/services/authService';
import { writeClipboardText } from '@/utils/clipboard';
import appIcon from '../../../../android/app/src/main/res/mipmap-xxxhdpi/ic_launcher.png';

interface NativeFilesystemPlugin {
  writeFile(options: { path: string; data: string; directory: string; recursive?: boolean }): Promise<{ uri: string }>;
  deleteFile(options: { path: string; directory: string }): Promise<void>;
}

interface NativeSharePlugin {
  share(options: { title?: string; text?: string; files?: string[]; dialogTitle?: string }): Promise<void>;
}

const Filesystem = registerPlugin<NativeFilesystemPlugin>('Filesystem');
const Share = registerPlugin<NativeSharePlugin>('Share');

interface ReferralPageProps {
  className?: string;
}

const toPersianDigits = (value: number | string) =>
  String(value).replace(/[0-9]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)]);

const copyToClipboard = async (text: string) => {
  await writeClipboardText(text);
};

const ReferralAvatar = ({
  name,
  avatarUrl,
  className
}: {
  name: string;
  avatarUrl: string | null;
  className?: string;
}) => {
  const [hasImageError, setHasImageError] = useState(false);

  return (
    <div className={cn('relative overflow-hidden rounded-full bg-[#DDF9EF]', className)}>
      {avatarUrl && !hasImageError ? (
        <img
          src={avatarUrl}
          alt={name}
          className="h-full w-full object-cover"
          onError={() => setHasImageError(true)}
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center text-[#239E76]">
          <UserRound className="h-5 w-5" />
        </div>
      )}
    </div>
  );
};

const ReferralPage = ({ className }: ReferralPageProps) => {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const [shareState, setShareState] = useState<'idle' | 'shared' | 'copied'>('idle');

  const { data, isLoading, error, refetch } = useQuery<ReferralsMeResponse>({
    queryKey: ['referralsMe'],
    queryFn: getMyReferrals,
    staleTime: 1000 * 60 * 3
  });

  const stages = data?.reward_stages ?? [];
  const achievedCount = stages.filter((stage) => stage.achieved).length;
  const currentStageNumber = useMemo(() => {
    if (!stages.length) return 1;
    const firstPending = stages.find((stage) => !stage.achieved);
    return firstPending?.stage_number ?? stages[stages.length - 1].stage_number;
  }, [stages]);

  const referralCode = data?.my_referral_code ?? '';
  const referralUrl = `${window.location.origin}/welcome`;
  const referralShareText = referralCode
    ? `کد دعوت من در بوکا: ${referralCode}\n${referralUrl}`
    : `بوکا را با من تجربه کن: ${referralUrl}`;

  const stageTitle = (stageNumber: number) => {
    if (stageNumber === 1) return 'نفر اول';
    if (stageNumber === 2) return 'نفر دوم';
    if (stageNumber === 3) return 'نفر سوم';
    return 'نفرات بعدی';
  };

  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), 1800);
    return () => window.clearTimeout(timer);
  }, [copied]);

  useEffect(() => {
    if (shareState === 'idle') return;
    const timer = window.setTimeout(() => setShareState('idle'), 2000);
    return () => window.clearTimeout(timer);
  }, [shareState]);

  const handleCopyCode = async () => {
    if (!referralCode) return;
    try {
      await copyToClipboard(referralCode);
      setCopied(true);
      setShareState('copied');
    } catch (err) {
      console.warn('Failed to copy referral code', err);
    }
  };

  const handleShareCodeWithImage = async () => {
    if (!referralCode) return;
    const shareText = `\u0628\u0627 \u06a9\u062f \u062f\u0639\u0648\u062a \u0645\u0646 \u06f3 \u0631\u0648\u0632 \u0627\u0634\u062a\u0631\u0627\u06a9 \u0631\u0627\u06cc\u06af\u0627\u0646 \u062f\u0631\u06cc\u0627\u0641\u062a \u06a9\u0646 \u0021\n\u06a9\u062f \u062f\u0639\u0648\u062a : ${referralCode}\n\n\u2b07\ufe0f \u062f\u0627\u0646\u0644\u0648\u062f \u0627\u0632 \u0628\u0627\u0632\u0627\u0631 : https://cafebazaar.ir/app/ir.booka.app\n\u2b07\ufe0f \u062f\u0627\u0646\u0644\u0648\u062f \u0627\u0632 \u0645\u0627\u06cc\u06a9\u062a : https://myket.ir/app/ir.booka.app`;
    // بعد از تأیید پیش‌نمایش، لینک‌های متنی زیر را در ShareData فعال می‌کنیم:
    // دانلود از بازار -> https://cafebazaar.ir/app/ir.booka.app
    // دانلود از مایکت -> https://myket.ir/app/ir.booka.app
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas is not supported');
    canvas.width = 420;
    canvas.height = 440;
    ctx.fillStyle = '#35D6A0';
    ctx.fillRect(0, 0, 1080, 1350);
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.roundRect(70, 70, 940, 1210, 48);
    ctx.fill();
    const logo = new Image();
    logo.src = appIcon;
    await new Promise<void>((resolve) => { logo.onload = () => resolve(); logo.onerror = () => resolve(); });
    if (logo.complete && logo.naturalWidth > 0) ctx.drawImage(logo, 410, 150, 260, 260);
    ctx.direction = 'rtl';
    ctx.textAlign = 'center';
    ctx.fillStyle = '#17352D';
    ctx.font = '700 48px Vazirmatn, sans-serif';
    ctx.fillText('دوستت را به بوکا دعوت کن', 540, 520);
    ctx.font = '400 34px Vazirmatn, sans-serif';
    ctx.fillStyle = '#52635D';
    ctx.fillText('۳ روز اشتراک رایگان هدیه بگیر', 540, 585);
    ctx.fillStyle = '#E9FBF4';
    ctx.roundRect(150, 690, 780, 250, 32);
    ctx.fill();
    ctx.fillStyle = '#52635D';
    ctx.font = '400 30px Vazirmatn, sans-serif';
    ctx.fillText('کد دعوت شما', 540, 765);
    ctx.fillStyle = '#17352D';
    ctx.font = '700 76px monospace';
    ctx.fillText(referralCode, 540, 860);
    ctx.fillStyle = '#35D6A0';
    ctx.font = '700 34px Vazirmatn, sans-serif';
    ctx.fillText('بوکا؛ هر روز یک قدم به خودت نزدیک‌تر', 540, 1090);
    // Compact themed share card with rounded corners and a square app icon.
    ctx.clearRect(0, 0, 420, 440);
    ctx.fillStyle = '#7063FF';
    ctx.beginPath();
    ctx.roundRect(0, 0, 420, 440, 28);
    ctx.fill();
    if (logo.complete && logo.naturalWidth > 0) {
      ctx.save();
      ctx.beginPath();
      ctx.roundRect(160, 28, 100, 100, 18);
      ctx.clip();
      ctx.drawImage(logo, 160, 28, 100, 100);
      ctx.restore();
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 5;
      ctx.beginPath();
      ctx.roundRect(160, 28, 100, 100, 18);
      ctx.stroke();
    }
    ctx.direction = 'rtl';
    ctx.textAlign = 'center';
    ctx.fillStyle = '#ffffff';
    ctx.font = '700 20px IRANSansX, sans-serif';
    ctx.fillText('\u0628\u0627 \u06a9\u062f \u062f\u0639\u0648\u062a \u0645\u0646 \u06f3 \u0631\u0648\u0632 \u0627\u0634\u062a\u0631\u0627\u06a9 \u0631\u0627\u06cc\u06af\u0627\u0646 \u062f\u0631\u06cc\u0627\u0641\u062a \u06a9\u0646 \u0021', 210, 170);
    ctx.font = '700 22px IRANSansX, sans-serif';
    ctx.fillText('\u06a9\u062f \u062f\u0639\u0648\u062a', 210, 235);
    ctx.font = '700 38px IRANSansX, sans-serif';
    ctx.fillText(referralCode, 210, 295);
    ctx.font = '700 23px IRANSansX, sans-serif';
    ctx.fillText('\u0628\u0648\u06a9\u0627\u061b\u062f\u0646\u06cc\u0627\u06cc \u062c\u062f\u06cc\u062f \u06a9\u062a\u0627\u0628 \u0647\u0627', 210, 370);

    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((result) => result ? resolve(result) : reject(new Error('Failed to create share image')), 'image/png');
    });
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject(new Error('Failed to read share image'));
      reader.readAsDataURL(blob);
    });
    if (Capacitor.isNativePlatform()) {
      const fileName = `booka-referral-${Date.now()}.png`;
      const file = await Filesystem.writeFile({
        path: fileName,
        data: dataUrl.split(',')[1],
        directory: 'CACHE',
        recursive: true,
      });
      try {
        await Share.share({
          title: 'دعوت به بوکا',
          text: shareText,
          files: [file.uri],
          dialogTitle: 'اشتراک‌گذاری کد دعوت',
        });
      } finally {
        await Filesystem.deleteFile({ path: fileName, directory: 'CACHE' }).catch(() => {});
      }
      setShareState('shared');
      return;
    }

    const nav = navigator as Navigator & {
      canShare?: (data: ShareData) => boolean;
      share?: (data?: ShareData) => Promise<void>;
    };
    const shareFile = new File([blob], 'booka-referral.png', { type: 'image/png' });
    if (typeof nav.share === 'function') {
      if (!nav.canShare || nav.canShare({ files: [shareFile] })) {
        await nav.share({ title: 'دعوت به بوکا', text: shareText, files: [shareFile] });
      } else {
        await nav.share({ title: 'دعوت به بوکا', text: shareText, url: referralUrl });
      }
      setShareState('shared');
    } else {
      await copyToClipboard(shareText);
      setCopied(true);
      setShareState('copied');
    }
  };

  const handleShareCode = async () => {
    try {
      await handleShareCodeWithImage();
      return;
    } catch (err) {
      console.warn('Failed to share referral code', err);
      return;
    }
    /* Legacy text fallback retained for browsers with no canvas support. */
    try {
      if (navigator.share) {
        await navigator.share({
          title: 'دعوت دوستان',
          text: referralShareText
        });
        setShareState('shared');
        return;
      }

      await copyToClipboard(referralShareText);
      setCopied(true);
      setShareState('copied');
    } catch (err) {
      console.warn('Failed to share referral code', err);
    }
  };

  const renderStageItem = (stage: ReferralStage, index: number) => {
    const isDone = stage.achieved;
    return (
      <div key={stage.stage_number} className="flex-1">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.28, delay: index * 0.05 }}
          className="flex flex-col items-center"
        >
          <div className="mb-3 flex min-h-[48px] flex-col items-center justify-end text-center">
            <div className="flex items-center gap-1.5 font-persian text-[11px] font-semibold text-foreground">
              <Gem className={cn('h-3.5 w-3.5', isDone ? 'text-[#35D6A0]' : 'text-gray-300')} />
              <span>{toPersianDigits(stage.gems)}</span>
            </div>
            <div className="mt-1 flex items-center gap-1 font-persian text-[10px] text-muted">
              <span>{stageTitle(stage.stage_number)}</span>
              {isDone && <Check className="h-3.5 w-3.5 text-[#35D6A0]" />}
            </div>
          </div>

          <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100">
            <div
              className={cn(
                'h-full rounded-full transition-all duration-500',
                isDone ? 'bg-[#35D6A0]' : 'bg-transparent'
              )}
              style={{ width: isDone ? '100%' : '0%' }}
            />
          </div>
        </motion.div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#F5FFFB] flex items-center justify-center" dir="rtl">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F5FFFB] flex items-center justify-center p-4" dir="rtl">
        <ErrorDisplay message={error instanceof Error ? error.message : 'خطا در دریافت اطلاعات رفرال'} onRetry={refetch} />
      </div>
    );
  }

  return (
    <div className={cn('min-h-screen bg-[#F5FFFB] pb-28', className)} dir="rtl">

      <div className="relative mx-auto flex min-h-screen w-full max-w-md flex-col px-5 pt-safe-top">
        <div className="mb-5 flex items-center justify-between mt-5">
          <BackArrow onClick={() => navigate(-1)} className="bg-white/80 backdrop-blur-sm shadow-sm" />
          <div className="h-11 w-11" />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="mb-4 rounded-[28px] border border-white/80 bg-white/90 p-5 shadow-[0_20px_60px_-32px_rgba(149,109,44,0.35)] backdrop-blur-sm"
        >
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="max-w-[16rem] font-persian text-[12px] leading-6 text-muted">
                دوستاتو دعوت کن تا برای هر دعوت الماس بگیری و به دوستات ۳ روز اشتراک رایگان هدیه بدی
              </p>
            </div>
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#DDF9EF] text-[#35D6A0]">
              <Users2 className="h-7 w-7" />
            </div>
          </div>

            <div className="mt-5 rounded-3xl border border-[#B9F1DD] bg-[#E9FBF4] p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="font-persian text-[11px] text-muted">کد دعوت شما</div>
                <div className="mt-1 font-mono text-2xl font-semibold tracking-[0.2em] text-foreground">
                  {referralCode || '---'}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleCopyCode}
                  className="flex h-11 w-11 items-center justify-center rounded-2xl border border-[#B9F1DD] bg-white text-[#239E76] shadow-sm transition-transform hover:scale-105"
                  aria-label="کپی کد"
                >
                  <Copy className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={handleShareCode}
                  className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#35D6A0] text-white shadow-sm transition-transform hover:scale-105"
                  aria-label="اشتراک گذاری کد"
                >
                  <Share2 className="h-4 w-4" />
                </button>
              </div>
            </div>
            <AnimatePresence>
              {(copied || shareState !== 'idle') && (
                <motion.p
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  className="mt-3 font-persian text-[11px] text-emerald-700"
                >
                  {shareState === 'shared' ? 'کد دعوت با موفقیت به اشتراک گذاشته شد.' : 'کد کپی شد.'}
                </motion.p>
              )}
            </AnimatePresence>
          </div>

          {/* Store download links are intentionally hidden until sharing is approved. */}
          {/*
            <a
              href="https://cafebazaar.ir/app/ir.booka.app"
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-2 rounded-2xl border border-[#B9F1DD] bg-white px-3 py-2 text-[#17352D] transition-colors hover:bg-[#F5FFFB]"
            >
              <img src={cafeBazaarIcon} alt="کافه بازار" className="h-8 w-8 rounded-lg object-contain" />
              <span className="font-persian text-[11px] font-semibold">دانلود از بازار</span>
            </a>
            <a
              href="https://myket.ir/app/ir.booka.app"
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-2 rounded-2xl border border-[#B9F1DD] bg-white px-3 py-2 text-[#17352D] transition-colors hover:bg-[#F5FFFB]"
            >
              <img src={myketIcon} alt="مایکت" className="h-8 w-8 rounded-lg object-contain" />
              <span className="font-persian text-[11px] font-semibold">دانلود از مایکت</span>
            </a>
          */}
        </motion.div>

        <section className="mb-4 rounded-[28px] border border-gray-100 bg-white/90 p-4 shadow-sm backdrop-blur-sm">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h2 className="font-persian text-base font-bold text-foreground">مرحله‌های پاداش</h2>
              <p className="mt-1 font-persian text-[11px] text-muted">
                {toPersianDigits(achievedCount)} از {toPersianDigits(stages.length)} مرحله تکمیل شده
              </p>
            </div>
            <div className="rounded-full bg-[#E9FBF4] px-3 py-1.5 font-persian text-[11px] font-medium text-[#239E76]">
              مرحله فعلی: {toPersianDigits(currentStageNumber)}
            </div>
          </div>

          <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${Math.max(stages.length, 1)}, minmax(0, 1fr))` }}>
            {stages.length > 0 ? stages.map(renderStageItem) : null}
          </div>
        </section>

        <section className="mb-4 grid grid-cols-2 gap-3">
          <div className="rounded-[24px] border border-gray-100 bg-white p-4 shadow-sm">
            <div className="font-persian text-[11px] text-muted">تعداد دعوت‌ها</div>
            <div className="mt-2 text-3xl font-bold text-foreground">{toPersianDigits(data?.total_referrals ?? 0)}</div>
          </div>
          <div className="rounded-[24px] border border-gray-100 bg-white p-4 shadow-sm">
            <div className="font-persian text-[11px] text-muted">الماس دریافت‌شده</div>
            <div className="mt-2 text-3xl font-bold text-foreground">{toPersianDigits(data?.total_reward_gems ?? 0)}</div>
          </div>
        </section>

        <section className="mb-4 rounded-[28px] border border-gray-100 bg-white/90 p-4 shadow-sm">
          <div className="mb-4">
            <h2 className="font-persian text-base font-bold text-foreground">دعوت‌شده‌ها</h2>
          </div>

          {data?.referrals?.length ? (
            <div className="space-y-2">
              {data.referrals.map((item, index) => (
                <motion.div
                  key={`${item.name}-${index}`}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25, delay: index * 0.04 }}
                  className="flex items-center gap-3 rounded-2xl border border-gray-100 bg-[#FCFBF8] p-3"
                >
                  <ReferralAvatar name={item.name} avatarUrl={item.avatar_url} className="h-11 w-11" />
                  <div className="min-w-0 flex-1 text-right">
                    <div className="truncate font-persian text-sm font-semibold text-foreground">{item.name}</div>
                    <div className="mt-0.5 font-persian text-[11px] text-muted">با کد شما وارد شده</div>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 px-4 py-6 text-center">
              <div className="font-persian text-sm font-medium text-foreground">هنوز کسی با کد شما ثبت‌نام نکرده</div>
              <div className="mt-1 font-persian text-[11px] text-muted">
                کدتان را برای دوستانتان ارسال کنید تا اولین پاداش‌ها را بگیرید.
              </div>
            </div>
          )}
        </section>

        <section className="mb-4 rounded-[28px] border border-gray-100 bg-white/90 p-4 shadow-sm">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h2 className="font-persian text-base font-bold text-foreground">لیگ دعوت‌کنندگان</h2>
            </div>
            <Trophy className="h-5 w-5 text-[#35D6A0]" />
          </div>

          <div className="flex max-h-[420px] flex-col overflow-hidden">
            <div className="flex-1 overflow-y-auto">
              {data?.referral_league?.length ? (
                <div className="divide-y divide-gray-100">
                  {data.referral_league.map((item) => (
                    <div key={`${item.rank}-${item.name}`} className="flex items-center gap-3 py-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white font-sans text-sm font-bold text-foreground shadow-sm">
                        {toPersianDigits(item.rank)}
                      </div>
                      <ReferralAvatar name={item.name} avatarUrl={item.avatar_url} className="h-11 w-11" />
                      <div className="min-w-0 flex-1 text-right">
                        <div className="truncate font-persian text-sm font-semibold text-foreground">{item.name}</div>
                      </div>
                      <div className="flex items-center gap-1 rounded-full border border-[#B9F1DD] bg-white px-3 py-1 text-sm font-bold text-[#239E76] shadow-sm">
                        <Gem className="h-3.5 w-3.5" />
                        {toPersianDigits(item.gems)}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 px-4 py-6 text-center font-persian text-sm text-muted">
                  هنوز داده‌ای برای لیگ ثبت نشده
                </div>
              )}
            </div>

            {data?.current_user_league &&
              data.current_user_league.rank != null &&
              data.current_user_league.gems != null && (
                <div className="shrink-0 border-t border-gray-100 pt-3 mt-3">
                  <div className="flex items-center gap-3 py-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#E9FBF4] font-sans text-sm font-bold text-[#239E76] shadow-sm ring-1 ring-[#B9F1DD]">
                      {toPersianDigits(data.current_user_league.rank)}
                    </div>
                    <ReferralAvatar
                      name={data.current_user_league.name}
                      avatarUrl={data.current_user_league.avatar_url}
                      className="h-11 w-11 ring-2 ring-[#9AEBCB] shadow-[0_0_0_4px_rgba(233,251,244,0.9)]"
                    />
                    <div className="min-w-0 flex-1 text-right">
                      <div className="truncate font-persian text-sm font-semibold text-foreground">
                        {data.current_user_league.name}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 rounded-full border border-[#B9F1DD] bg-white px-3 py-1 text-sm font-bold text-[#239E76] shadow-sm">
                      <Gem className="h-3.5 w-3.5" />
                      {toPersianDigits(data.current_user_league.gems)}
                    </div>
                  </div>
                </div>
              )}
          </div>
        </section>
      </div>

    </div>
  );
};

export default ReferralPage;
